/**
 * Every number, date and amount the user sees is formatted here.
 *
 * Centralised for one reason: `Intl` needs a locale, and a locale scattered
 * across forty components is a locale that is wrong in thirty of them. When a
 * second language is added, the formatters follow it because they read it from
 * one place.
 *
 * Formatters are cached. Constructing an `Intl.NumberFormat` is comparatively
 * expensive, and a table renders one per cell otherwise.
 */
import type { MoneyRead } from '@/client'
import { isTimezone } from '@/lib/timezone'

const numberFormatters = new Map<string, Intl.NumberFormat>()
const dateFormatters = new Map<string, Intl.DateTimeFormat>()

function numberFormatter(locale: string, options: Intl.NumberFormatOptions) {
  const key = `${locale}:${JSON.stringify(options)}`
  let formatter = numberFormatters.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options)
    numberFormatters.set(key, formatter)
  }
  return formatter
}

function dateFormatter(locale: string, options: Intl.DateTimeFormatOptions) {
  const key = `${locale}:${JSON.stringify(options)}`
  let formatter = dateFormatters.get(key)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options)
    dateFormatters.set(key, formatter)
  }
  return formatter
}

/**
 * Render an amount from the API.
 *
 * The API sends `{ amount: "129.9900", currency: "USD" }` — a **string**
 * amount, because a JSON number would be an IEEE double. It is parsed to a
 * float only here, at the last moment before display, where a sub-cent
 * rounding difference cannot propagate into anything.
 *
 * Never do arithmetic on the result. If two amounts need adding, the backend
 * adds them.
 */
export function formatMoney(money: MoneyRead, locale = 'en'): string {
  const amount = Number(money.amount)
  if (Number.isNaN(amount)) return `${money.amount} ${money.currency}`

  return numberFormatter(moneyLocale(money.currency, locale), {
    style: 'currency',
    currency: money.currency,
  }).format(amount)
}

/**
 * Rupees are grouped the Indian way — `₹1,24,500`, lakhs and crores — when
 * the reader is reading English (VentaHub's Principles board). Every other
 * currency, and every non-English reader, keeps the reader's own grouping:
 * it is the currency from the data that calls for it, never a default.
 */
function moneyLocale(currency: string, locale: string): string {
  return currency === 'INR' && isEnglish(locale) ? 'en-IN' : locale
}

function isEnglish(locale: string): boolean {
  return locale === 'en' || locale.startsWith('en-')
}

/**
 * Day, month and year as VentaHub writes them in English: `06 Oct 2026`.
 *
 * Built from the formatter's parts rather than a regional locale: `en-US`
 * puts the month first, and `en-GB` abbreviates September as "Sept". Other
 * languages keep their own order and month names.
 */
function usableZone(zone: string | undefined): string | undefined {
  return isTimezone(zone) ? zone : undefined
}

function dayMonthYear(date: Date, locale: string, zone?: string): string {
  const timeZone = usableZone(zone)
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  }
  if (!isEnglish(locale)) return dateFormatter(locale, options).format(date)

  const parts = dateFormatter(locale, options).formatToParts(date)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((piece) => piece.type === type)?.value ?? ''
  return `${part('day')} ${part('month')} ${part('year')}`
}

/**
 * A calendar day (`YYYY-MM-DD`, no time, no zone) as `06 Oct 2026`.
 *
 * Built from the day's parts, never through `new Date(day)`: that parses as
 * midnight UTC and shows the day before to anyone west of UTC.
 */
export function formatDay(day: string, locale = 'en'): string {
  const [year, month, date] = day.split('-').map(Number)
  if (!year || !month || !date) return day
  return dayMonthYear(new Date(year, month - 1, date), locale)
}

/** One part of a date as `locale` writes it with `options`. */
function datePart(date: Date, locale: string, options: Intl.DateTimeFormatOptions) {
  const parts = dateFormatter(locale, options).formatToParts(date)
  return (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((piece) => piece.type === type)?.value ?? ''
}

/**
 * A calendar day (`YYYY-MM-DD`) as `9 Oct`, without the year — for a label
 * whose year is plain from what is around it: a chart tick, a period chip
 * (VentaHub's `d MMM`). English is built from the parts, since `en-US` would
 * write "Oct 9"; other languages keep their own order. Never through
 * `new Date(day)`, for the reason `formatDay` gives.
 */
export function formatDayMonth(day: string, locale = 'en'): string {
  const [year, month, date] = day.split('-').map(Number)
  if (!year || !month || !date) return day
  const value = new Date(year, month - 1, date)
  const options = { day: 'numeric', month: 'short' } as const
  if (!isEnglish(locale)) return dateFormatter(locale, options).format(value)
  const part = datePart(value, locale, options)
  return `${part('day')} ${part('month')}`
}

/**
 * A calendar month (`YYYY-MM`) as `Oct '26` — a monthly chart tick
 * (VentaHub's `MMM ''yy`). English is built from the parts, since `en-US`
 * would write "Oct 26"; other languages keep their own short form.
 */
export function formatMonthYearShort(month: string, locale = 'en'): string {
  const [year, index] = month.split('-').map(Number)
  if (!year || !index) return month
  const value = new Date(year, index - 1, 1)
  const options = { month: 'short', year: '2-digit' } as const
  if (!isEnglish(locale)) return dateFormatter(locale, options).format(value)
  const part = datePart(value, locale, options)
  return `${part('month')} '${part('year')}`
}

/** A day as a chart axis writes it, short enough for thirty side by side: `Sep 16`. */
export function formatShortDay(day: string, locale = 'en'): string {
  const [year, month, date] = day.split('-').map(Number)
  if (!year || !month || !date) return day
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(
    new Date(year, month - 1, date),
  )
}

/** A month's name as a calendar header writes it: `October`. */
export function formatMonthName(month: number, locale = 'en'): string {
  return dateFormatter(locale, { month: 'long' }).format(new Date(2000, month, 1))
}

/**
 * A month from its first day, year first: `2026-08-01` → `2026 Aug`.
 *
 * Year first, as VentaHub's period picker has it: the list runs newest
 * first, and a year-leading label reads down the years cleanly across a
 * year boundary. Built from the parts, never `new Date(day)`, which parses
 * as UTC and shows the previous month west of it.
 */
export function formatMonthOfDay(day: string, locale = 'en'): string {
  const [year, month] = day.split('-').map(Number)
  if (!year || !month) return day
  return `${year} ${dateFormatter(locale, { month: 'short' }).format(new Date(year, month - 1, 1))}`
}

/** The short weekday names, Sunday first: `Sun`, `Mon`, … */
export function weekdayNames(locale = 'en'): string[] {
  // 2 Jan 2000 was a Sunday.
  return Array.from({ length: 7 }, (_, index) =>
    dateFormatter(locale, { weekday: 'short' }).format(new Date(2000, 0, 2 + index)),
  )
}

/**
 * A date, in the reader's locale: `06 Oct 2026`. The API sends an instant;
 * `zone` says whose calendar day it falls on — the page's, from `useZone()` —
 * and without one it is the browser's.
 */
export function formatDate(iso: string, locale = 'en', zone?: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso

  return dayMonthYear(date, locale, zone)
}

/**
 * A date and a 24-hour time, for anything where the hour matters: `06 Oct 2026, 10:42`.
 * `zone` is the IANA zone the clock reads in (a page's, from `useZone()`); without one,
 * the browser's. A zone the browser does not know falls back to the browser's rather
 * than throwing mid-render.
 */
export function formatDateTime(iso: string, locale = 'en', zone?: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso

  const timeZone = usableZone(zone)
  const time = dateFormatter(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    ...(timeZone ? { timeZone } : {}),
  }).format(date)
  return `${dayMonthYear(date, locale, timeZone)}, ${time}`
}

const relativeFormatters = new Map<string, Intl.RelativeTimeFormat>()

/**
 * How long ago, in words: `5 min. ago`, `2 hours ago`, `yesterday` — for a
 * feed whose reader cares how fresh something is, not when exactly. Under a
 * minute is `now`; a week or more is the date, where "23 days ago" makes the
 * reader count backwards. `now` is a parameter so a test can pin the clock.
 */
export function formatRelative(iso: string, locale = 'en', now: Date = new Date()): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  let formatter = relativeFormatters.get(locale)
  if (!formatter) {
    formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' })
    relativeFormatters.set(locale, formatter)
  }
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000)
  const ago = -seconds
  if (ago < 60) return formatter.format(0, 'second')
  if (ago < 3600) return formatter.format(Math.round(seconds / 60), 'minute')
  if (ago < 86_400) return formatter.format(Math.round(seconds / 3600), 'hour')
  if (ago < 7 * 86_400) return formatter.format(Math.round(seconds / 86_400), 'day')
  return dayMonthYear(date, locale)
}

/** A country's name from its ISO 3166-1 alpha-2 code — `DE` → `Germany`; the code when unknown. */
export function formatCountry(code: string, locale = 'en'): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code.toUpperCase()) ?? code
  } catch {
    return code
  }
}

/** A plain count, grouped for readability. */
export function formatNumber(value: number, locale = 'en'): string {
  return numberFormatter(locale, {}).format(value)
}

/**
 * A short figure — `1.2K`, `3.4M` on a chart axis; `432.35K` with two
 * decimals on a headline card. The full value belongs in the tooltip.
 */
export function formatCompact(value: number, locale = 'en', fractionDigits = 1): string {
  return numberFormatter(locale, {
    notation: 'compact',
    maximumFractionDigits: fractionDigits,
  }).format(value)
}

/**
 * A money amount, short — `$1.2K`, `₹3.2M` (lakhs and crores, `₹32L`, where
 * rupees are grouped the Indian way) — for a label with no room for the
 * whole figure. The full amount (`formatMoney`) belongs beside it on hover.
 * `fractionDigits: 2` gives a headline card's `$432.35K` and `₹32.49L`.
 */
export function formatMoneyCompact(money: MoneyRead, locale = 'en', fractionDigits = 1): string {
  const amount = Number(money.amount)
  if (Number.isNaN(amount)) return `${money.amount} ${money.currency}`
  return numberFormatter(moneyLocale(money.currency, locale), {
    style: 'currency',
    currency: money.currency,
    notation: 'compact',
    maximumFractionDigits: fractionDigits,
  }).format(amount)
}

/**
 * A currency's own sign, as a reader knows it — `$`, `€`, `₹`, `₺`, `kr` —
 * for a label beside its code. Narrow, so the dollars are all `$`: the code
 * beside it tells them apart. Undefined where the sign is only the code again
 * (`AED`) or the code is not one `Intl` knows.
 */
export function currencySymbol(code: string, locale = 'en'): string | undefined {
  try {
    const symbol = numberFormatter(locale, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value
    return symbol && symbol !== code ? symbol : undefined
  } catch {
    return undefined
  }
}

/**
 * A fraction as a percentage: `0.8123` → `81.2%`.
 *
 * Takes the fraction the API sends, not a pre-multiplied number, so the
 * "×100" happens in exactly one place.
 */
export function formatPercent(fraction: number, locale = 'en', fractionDigits = 1): string {
  return numberFormatter(locale, {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(fraction)
}

/** A measured value with a fixed number of decimals — a coefficient, a bid, a ratio. */
export function formatDecimal(value: number, locale = 'en', fractionDigits = 2): string {
  return numberFormatter(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)
}

/** A duration in milliseconds, as seconds with one decimal: `1450` → `1.5 sec`. */
export function formatDuration(ms: number, locale = 'en'): string {
  return numberFormatter(locale, {
    style: 'unit',
    unit: 'second',
    unitDisplay: 'short',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(ms / 1000)
}

/**
 * How long ago, as a freshness matrix writes it: `45m`, `9h 32m`, `13d 4h` —
 * the two largest units, narrow, so a column of ages stays one width.
 */
export function formatAge(ms: number, locale = 'en'): string {
  const minutes = Math.max(0, Math.floor(ms / 60_000))
  const unit = (value: number, name: 'minute' | 'hour' | 'day') =>
    numberFormatter(locale, { style: 'unit', unit: name, unitDisplay: 'narrow' }).format(value)
  if (minutes < 60) return unit(minutes, 'minute')
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${unit(hours, 'hour')} ${unit(minutes % 60, 'minute')}`
  return `${unit(Math.floor(hours / 24), 'day')} ${unit(hours % 24, 'hour')}`
}

/**
 * A time of day in 24-hour clock, for a moment on the current day: `10:42`.
 * `clock: 'locale'` reads the locale's own clock instead, 12- or 24-hour —
 * `02:30 AM` in English — where the time sits beside a full date.
 */
export function formatTime(iso: string, locale = 'en', clock: '24h' | 'locale' = '24h'): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso

  return dateFormatter(locale, {
    hour: '2-digit',
    minute: '2-digit',
    ...(clock === '24h' ? { hourCycle: 'h23' } : {}),
  }).format(date)
}

/**
 * A countdown in seconds as a clock: `72` → `1:12`, `9` → `0:09`.
 *
 * The locale shapes the digits only; the colon layout is what a reader
 * expects of a countdown everywhere. Negative values clamp to `0:00`.
 */
export function formatClock(seconds: number, locale = 'en'): string {
  const whole = Math.max(0, Math.round(seconds))
  const minutes = Math.floor(whole / 60)
  const rest = whole % 60
  const digits = numberFormatter(locale, { useGrouping: false })
  const twoDigits = numberFormatter(locale, { minimumIntegerDigits: 2, useGrouping: false })
  return `${digits.format(minutes)}:${twoDigits.format(rest)}`
}

/**
 * A VAT rate, sent as a fraction (`"0.18"`), as a percentage with as many
 * decimals as it has, up to two: `18%`, `5.5%`, `12.25%`. Missing is a dash.
 */
export function formatVatRate(fraction: string | null | undefined, locale = 'en'): string {
  if (fraction === null || fraction === undefined || fraction === '') return '—'
  return numberFormatter(locale, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(fraction))
}

/**
 * An amount that may be missing, in a currency that may be missing.
 *
 * Ops documents have nullable amounts and currencies on the same row. With both
 * it is a formatted amount; with an amount alone it is shown as sent rather than
 * under an invented currency; with neither it is a dash.
 */
export function formatAmount(
  amount: string | null | undefined,
  currency: string | null | undefined,
  locale = 'en',
): string {
  if (amount === null || amount === undefined) return '—'
  if (!currency) return amount
  return numberFormatter(locale, { style: 'currency', currency }).format(Number(amount))
}
